import React from 'react';
import tw from 'tailwind-styled-components';
import { FaExclamationTriangle, FaTimes, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';

const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
    title,
    message,
    children,
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "danger", // Variants: 'danger', 'success', 'warning', 'info'
    confirmButtonColor // Optional custom color for confirm button
}) => {
    if (!isOpen) return null;

    // Determine styles based on variant
    const getVariantStyles = () => {
        switch (variant) {
            case 'success':
                return {
                    icon: FaCheckCircle,
                    iconBg: 'bg-green-100',
                    iconColor: 'text-green-600',
                    btnColor: 'bg-green-600 hover:bg-green-700 ring-green-500'
                };
            case 'info':
                return {
                    icon: FaInfoCircle,
                    iconBg: 'bg-blue-100',
                    iconColor: 'text-blue-600',
                    btnColor: 'bg-kuality-blue hover:bg-kuality-dark ring-blue-500'
                };
            case 'warning':
                return {
                    icon: FaExclamationTriangle,
                    iconBg: 'bg-yellow-100',
                    iconColor: 'text-yellow-600',
                    btnColor: 'bg-yellow-600 hover:bg-yellow-700 ring-yellow-500'
                };
            case 'danger':
            default:
                return {
                    icon: FaExclamationTriangle,
                    iconBg: 'bg-red-100',
                    iconColor: 'text-red-600',
                    btnColor: 'bg-red-600 hover:bg-red-700 ring-red-500'
                };
        }
    };

    const styles = getVariantStyles();
    const Icon = styles.icon;
    // Use custom button color if provided
    const finalBtnClass = confirmButtonColor || styles.btnColor;

    return (
        <Overlay onClick={onClose}>
            <Container onClick={(e) => e.stopPropagation()}>
                {/* Close Icon */}
                <CloseButton onClick={onClose}>
                    <FaTimes className="w-4 h-4" />
                </CloseButton>

                <ContentWrapper>
                    <TextContent>
                        <span className='flex items-center text-center flex-row sm:text-left sm:items-start gap-2'>
                            {/* <IconWrapper className={styles.iconBg}>
                                <Icon className={`w-4 h-4 ${styles.iconColor}`} />
                            </IconWrapper> */}
                            <Title>{title}</Title>
                        </span>
                        <Message>{message}</Message>
                        {children && (
                            <ChildrenWrapper>
                                {children}
                            </ChildrenWrapper>
                        )}
                    </TextContent>
                </ContentWrapper>

                {/* Footer Action Buttons */}
                <Footer>
                    <ButtonContainer>
                        <CancelButton onClick={onClose}>
                            {cancelText}
                        </CancelButton>
                        <ConfirmButton
                            onClick={onConfirm}
                            disabled={isLoading}
                            className={`${finalBtnClass}`}
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <Spinner />
                                </div>
                            ) : (
                                confirmText
                            )}
                        </ConfirmButton>
                    </ButtonContainer>
                </Footer>
            </Container>
        </Overlay>
    );
};

// Styled Components
const Overlay = tw.div`
    fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm
    flex items-center justify-center z-50 p-4
    transition-opacity duration-300
`;

const Container = tw.div`
    bg-white rounded-xl shadow-2xl
    w-full max-w-sm md:max-w-md
    transform transition-all scale-100
    relative
`;

const CloseButton = tw.button`
    absolute top-4 right-4 p-2
    text-gray-400 hover:text-gray-600
    transition-colors rounded-full hover:bg-gray-100
    z-10
`;

const ContentWrapper = tw.div`
    p-4 pb-0 flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-start gap-1 md:gap-4
`;

const IconWrapper = tw.div`
    flex-shrink-0 flex items-center justify-center
    h-6 w-6 rounded-full
`;

const TextContent = tw.div`
    flex-1 w-full
`;

const Title = tw.h3`
    text-lg leading-6 font-bold text-gray-900 mb-2
`;

const Message = tw.p`
    text-sm text-gray-500 leading-relaxed
`;

const ChildrenWrapper = tw.div`
    my-2 text-sm text-gray-700 w-full text-left
`;

const Footer = tw.div`
    bg-gray-50 px-6 py-2
    flex flex-col-reverse sm:flex-row sm:justify-end gap-3
`;

const ButtonContainer = tw.div`
    flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto
`;

const ButtonBase = tw.button`
    w-full inline-flex justify-center items-center rounded-lg
    border px-4 py-2 text-base font-medium shadow-sm
    focus:outline-none 
    sm:w-auto sm:text-sm transition-all duration-200
`;

const CancelButton = tw(ButtonBase)`
    border-gray-300 bg-white text-gray-700
    hover:bg-gray-50 hover:text-gray-900
    focus:ring-2 focus:ring-offset-2 focus:ring-gray-200
`;

const ConfirmButton = tw(ButtonBase)`
    border-transparent text-white
    disabled:opacity-70 disabled:cursor-not-allowed
`;

const Spinner = tw.div`
    animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2
`;

export default ConfirmationModal;